/**
 * Scheduling & auto-publish types — mirror the backend's application-level
 * string enums (src/utils/enums/Scheduling.ts) and the wire shapes of
 * /api/scheduling. Same mirrored-enum convention as contentCalendar.type.ts.
 */

import type { CalendarPlatform, SlotOrigin } from "./contentCalendar.type";

export type DeliverableKind = "VIDEO" | "IMAGE";
export type DeliverableStatus = "UPLOADING" | "PROCESSING" | "READY" | "FAILED";

export type PublicationStatus =
  | "DRAFT"
  | "READY"
  | "SCHEDULED"
  | "PUBLISHING"
  | "PUBLISHED"
  | "FAILED"
  | "CANCELLED";

export const PUBLICATION_STATUS_LABEL: Record<PublicationStatus, string> = {
  DRAFT: "Draft",
  READY: "Ready",
  SCHEDULED: "Scheduled",
  PUBLISHING: "Publishing",
  PUBLISHED: "Published",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

/**
 * BLOCK holds the publication on DRAFT; WARN publishes anyway. Optional because
 * rows written before severity existed carry none, and back then everything
 * blocked — so absent reads as BLOCK.
 */
export type IssueSeverity = "BLOCK" | "WARN";

export interface PublicationIssue {
  code: string;
  message: string;
  severity?: IssueSeverity;
}

export function isBlockingIssue(issue: PublicationIssue): boolean {
  return issue.severity !== "WARN";
}

/** Snapchat posts to one of two destinations, and the choice rides on the payload. */
export type SnapchatSurface = "STORY" | "SPOTLIGHT";

/**
 * Per-platform copy payload. IG/FB/TikTok use caption+hashtags; YouTube uses
 * title+description+tags; Snapchat uses description+hashtags plus its surface,
 * locale and save-to-profile switch.
 */
export interface PublicationFields {
  caption?: string;
  hashtags?: string[];
  title?: string;
  description?: string;
  tags?: string[];
  surface?: SnapchatSurface;
  locale?: string;
  saveToProfile?: boolean;
  /** TikTok: the moment of the video used as the cover, in ms. Null: TikTok picks. */
  coverTimestampMs?: number | null;
}

/** The post's copy for one platform (SlotChannel), with or without a publication. */
export interface ChannelCopy {
  platform: CalendarPlatform;
  copy: PublicationFields;
  updatedAt: string | null;
  /** False for platforms posted by hand. */
  publishable: boolean;
}

/** The most slides a post can have (backend MAX_SLOT_ITEMS). */
export const MAX_SLIDES = 20;

/** One slide of a post, in order. Two or more make it a carousel. */
export interface SlotItem {
  id: string;
  position: number;
  kind: DeliverableKind;
  label: string;
  briefPanelId: string | null;
  /** The picture on this slide; null for an empty slide. */
  deliverableId: string | null;
}

/** What one platform of a post posts. */
export type ChannelShape = "VIDEO" | "IMAGE" | "CAROUSEL";

export interface Publication {
  id: string;
  /** The file this platform publishes. */
  deliverableId: string;
  slotId?: string;
  /**
   * The platform's file was set for it (a person picked it, or it was uploaded
   * for this platform) rather than following the file for every platform.
   * Absent on reads from before a post could hold several files.
   */
  fileChosen?: boolean;
  platform: CalendarPlatform;
  socialAccountId: string | null;
  accountName?: string | null;
  accountExternalId?: string | null;
  fields: PublicationFields;
  /** ISO UTC instant to post at. */
  scheduledAt: string;
  /** Who scheduled it, snapshotted when it was scheduled. */
  scheduledByUserName?: string | null;
  status: PublicationStatus;
  /**
   * Post it yourself (Figma 08 Live): the team posts this channel by hand; at
   * its time the bell has the file and caption ready, then Mark as posted.
   */
  selfPost?: boolean;
  issues: PublicationIssue[];
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  externalPostId: string | null;
  permalink: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SlotDeliverable {
  id: string;
  slotId: string;
  kind: DeliverableKind;
  status: DeliverableStatus;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
  processingError: string | null;
  thumbnailKey: string | null;
  createdAt: string;
  updatedAt: string;
}

/** NO_FILE: the platform has no file (none of its own, and no default). */
export type SkippedChannelReason = "NOT_PUBLISHABLE" | "NO_ACCOUNT" | "NEEDS_FANOUT" | "NO_FILE";
export interface SkippedChannel {
  platform: CalendarPlatform;
  reason: SkippedChannelReason;
}

/**
 * Why a platform will keep its own frame instead of the post's cover (backend
 * application/SocialConnection/Utils/youtubeCoverLock.ts). YouTube only:
 * CHANNEL_NOT_VERIFIED until the channel is phone-verified, CHANNEL_RESTRICTED
 * while strikes or Content ID blocks hold its features back.
 */
export type CoverLockReason = "CHANNEL_NOT_VERIFIED" | "CHANNEL_RESTRICTED";
export interface CoverLock {
  platform: CalendarPlatform;
  reason: CoverLockReason;
}

/** The short-lived signed read URLs a file carries on the distribution read. */
export interface DeliverableUrls {
  /** Previews the file. Regenerated on every read, never persisted. */
  mediaUrl: string | null;
  /** A video's cover. */
  thumbnailUrl: string | null;
}

/** One file on a post. A post holds many: one for every platform, and any per platform. */
export interface SlotFile extends SlotDeliverable, DeliverableUrls {
  /** The platform it was uploaded for; null for every platform. */
  forPlatform: CalendarPlatform | null;
  /**
   * The display default: the READY every-platform file, else the newest
   * every-platform file, else the newest file. Not always for every platform.
   */
  isDefault: boolean;
  /** Always null on the read: removed files are not listed. */
  deletedAt?: string | null;
}

/** A hand-posted channel's receipt (backend domain/Scheduling/manualPublications.ts). */
export interface ManualPublicationCompletion {
  platform: CalendarPlatform;
  publishedAt: string;
  permalink: string | null;
  recordedAt: string;
  recordedByUserId: string;
  recordedByUserName: string | null;
}

/**
 * The file and cover one platform takes, set by POST /files/match (backend
 * domain/Scheduling/platformFiles.ts). Null when no file on the post fits.
 */
export interface PlatformFile {
  platform: CalendarPlatform;
  deliverableId: string | null;
  /** A picture on a video post, used as the platform's cover. */
  coverDeliverableId: string | null;
}

/** GET /slots/:slotId/distribution response. */
export interface DistributionResult {
  briefAccepted: boolean;
  origin: SlotOrigin;
  /** The platforms the post targets: the accepted idea's, or a quick post's own. */
  channels: CalendarPlatform[];
  /** ISO day (00:00 UTC) the slot is planned for — default publish day. */
  slotDay: string;
  /**
   * The display default (the same file `files` marks `isDefault`), null only
   * when the post has no file. Its `publications` are all of the post's
   * publications, whichever file each one uses.
   */
  deliverable: (SlotDeliverable & DeliverableUrls & { publications: Publication[] }) | null;
  /**
   * Every file on the post, newest first. Absent from servers that hold one
   * file per post; read it through distributionFiles().
   */
  files?: SlotFile[];
  /**
   * What each platform takes once the files were matched. A platform missing
   * here (or a post never matched) follows its publication's file.
   */
  platformFiles?: PlatformFile[];
  /**
   * NO_FILE: the platform has no file (also before anything is uploaded).
   * NEEDS_FANOUT: no publication yet, also while the platform's only file is
   * still being checked.
   */
  skippedChannels: SkippedChannel[];
  /** Channels whose account won't take the cover. Absent from older servers. */
  coverLocks?: CoverLock[];
  manualCompletions: ManualPublicationCompletion[];
  /** The post's copy for each platform. Absent from older servers. */
  channelCopy?: ChannelCopy[];
  /** The post's slides, in order (two or more make it a carousel). Absent from older servers. */
  items?: SlotItem[];
  /** What each platform posts. Absent from older servers. */
  channelShapes?: Partial<Record<CalendarPlatform, ChannelShape>>;
  /** The most slides each carousel platform takes. */
  carouselLimits?: Partial<Record<CalendarPlatform, number>>;
}

/** Every file on the post, newest first. A one-file server's `deliverable` is that file, for every platform. */
export function distributionFiles(data: DistributionResult | null | undefined): SlotFile[] {
  if (!data) return [];
  if (data.files) return data.files;
  return data.deliverable ? [{ ...data.deliverable, forPlatform: null, isDefault: true }] : [];
}

/** Every publication on the post, whichever file each one uses (they ride on `deliverable`). */
export function distributionPublications(data: DistributionResult | null | undefined): Publication[] {
  return data?.deliverable?.publications ?? [];
}

/** GET /brands/:brandId/publishable-accounts row. */
export interface PublishableAccount {
  accountId: string;
  type: string;
  name: string | null;
  username: string | null;
  pictureUrl: string | null;
  provider: string;
  /** The connection holds the publish scope; false ⇒ "Reconnect to enable publishing". */
  canPublish: boolean;
  requiredScope: string | null;
  connectionHealthy: boolean;
}

/** PATCH /publications/:id body. scheduledAt is brand-local; the server converts. */
export interface UpdatePublicationInput {
  fields?: PublicationFields;
  scheduledAt?: { day: string; time: string };
  socialAccountId?: string;
  /** The platform's file: a READY file on the post. Only while the publication is DRAFT, READY or FAILED. */
  deliverableId?: string;
}

/** Upload ticket from POST /slots/:slotId/deliverable/upload-url. */
export interface DeliverableUploadTicket {
  uploadUrl: string;
  uploadToken: string;
}

/** Upload ticket from POST /slots/:slotId/deliverable/thumbnail/upload-url. */
export interface DeliverableThumbnailTicket {
  uploadUrl: string;
  thumbnailToken: string;
}

export const PLATFORM_FIELD_LIMITS: Partial<
  Record<
    CalendarPlatform,
    {
      captionMax?: number;
      maxHashtags?: number;
      titleMax?: number;
      descriptionMax?: number;
      /** YouTube: total characters across all tags. */
      tagsMaxChars?: number;
      requiresTitle?: boolean;
      /** Which key holds the body copy. Defaults to caption. */
      bodyField?: "caption" | "description";
      /** Which key holds the keyword list. Defaults to hashtags. */
      tagsField?: "hashtags" | "tags";
      /** The hashtags render into the body, so the limit covers both together. */
      tagsCountTowardBody?: boolean;
      /** The platform asks which destination to post to. */
      surfaces?: SnapchatSurface[];
    }
  >
> = {
  INSTAGRAM: { captionMax: 2200, maxHashtags: 30 },
  FACEBOOK: { captionMax: 63206, maxHashtags: 30 },
  TIKTOK: { captionMax: 2200, maxHashtags: 30 },
  YOUTUBE: {
    titleMax: 100,
    descriptionMax: 5000,
    tagsMaxChars: 500,
    requiresTitle: true,
    bodyField: "description",
    tagsField: "tags",
  },
  SNAPCHAT: {
    descriptionMax: 160,
    maxHashtags: 30,
    bodyField: "description",
    tagsCountTowardBody: true,
    surfaces: ["STORY", "SPOTLIGHT"],
  },
};

/**
 * What Snapchat actually receives: the description with hashtags appended.
 * Mirrors renderSnapchatDescription on the backend so the counter in the editor
 * measures exactly what the 160-char limit is applied to.
 */
export function renderSnapchatDescription(
  description: string,
  hashtags: string[],
): string {
  const tags = hashtags
    .map((tag) => tag.trim().replace(/^#/, ""))
    .filter(Boolean)
    .map((tag) => `#${tag}`)
    .join(" ");
  return [description.trim(), tags].filter(Boolean).join(" ");
}

/** Extra copy for issue codes the panel should explain, not just echo. */
export const PUBLICATION_ISSUE_HELP: Record<string, string> = {
  SURFACE_MEDIA_KIND:
    "Snapchat Spotlight takes video only. Switch this post to a Story, or upload a video.",
  PROBE_INCOMPLETE:
    "Re-export the video (H.264 + AAC in an MP4) and upload again so we can read duration, codec, and size.",
  KIND_MISMATCH:
    "This file doesn't match the post's planned format. Upload a video onto a video post, or an image onto a static post.",
  ASPECT_RATIO_SUBOPTIMAL:
    "This still publishes — the platform just adds bars or crops to fit. Re-export in the listed frame if you want it edge to edge.",
};

export const PLATFORM_ACCOUNT_TYPE: Partial<Record<CalendarPlatform, string>> = {
  INSTAGRAM: "INSTAGRAM_BUSINESS",
  FACEBOOK: "FACEBOOK_PAGE",
  TIKTOK: "TIKTOK_ACCOUNT",
  YOUTUBE: "YOUTUBE_CHANNEL",
  SNAPCHAT: "SNAPCHAT_PUBLIC_PROFILE",
};

